
























import Vue from 'vue';
import moment from 'moment';
import { DateFormat } from '../../constants/dateFormat';
import { DatePickerDayModel } from '../../types/datePicker';
import DatePickerContainer from '@/components/landing/DatePickerContainer.vue';
import { DatepickerSearchPeriod } from '@/constants/calendarFilterPeriod';

export default Vue.extend({
  name: 'DatePicker',
  components: {
    DatePickerContainer,
  },
  props: {
    showTwoMonth: {
      type: Boolean,
      default: false,
    },
    canNavigate: {
      type: Boolean,
      default: true,
    },
    rangePicker: {
      type: Boolean,
      default: false,
    },
    minDate: Date,
    maxDate: Date,
    preselectedDate: [String, Object],
    yearsAgo: {
      type: Number,
      default: 10,
    },
    yearsAfter: {
      type: Number,
      default: 10,
    },
    combinedSelector: {
      type: Boolean,
      default: false,
    },
    targetScope: {
      type: String,
    },
  },
  data: () => ({
    startDate: new Date(),
    currentDate: moment(),
    nextDate: moment(),
    currentWeeks: [] as DatePickerDayModel[][],
    nextWeeks: [] as DatePickerDayModel[][],
    hoveredDay: {} as DatePickerDayModel,
    selectedDay: {} as DatePickerDayModel,
    selectedSecondDay: {} as DatePickerDayModel,
    weekdays: [],
    months: [],
  }),
  computed: {
    isSaveDisabled(): boolean {
      if (!this.selectedDay.date) {
        return true;
      }
      return !!(this.rangePicker && !this.selectedSecondDay.date);
    },
    maxRangeDate(): Date {
      if (this.rangePicker) {
        return new Date(this.maxDate);
      }
      const maxRangeDate = this.startDate ? new Date(this.startDate) : new Date();
      maxRangeDate.setHours(0, 0, 0, 0);
      return new Date(maxRangeDate.setMonth(maxRangeDate.getMonth() + 1));
    },
    checkinActive(): boolean {
      return this.targetScope === DatepickerSearchPeriod.Checkin;
    },
  },
  watch: {
    '$i18n.locale'(newV, oldV) {
      if (newV !== oldV) {
        this.prepareLocale();
        this.currentDate = moment(this.currentDate);
        if (this.showTwoMonth) {
          this.nextDate = moment(this.nextDate);
        }
      }
    },
  },
  created() {
    this.prepareLocale();
    if (this.preselectedDate) {
      if (typeof this.preselectedDate === 'string') {
        this.selectedDay = { date: moment(new Date(this.preselectedDate)) };
        this.currentDate = moment(new Date(this.preselectedDate));
      } else {
        if (this.preselectedDate.from) {
          this.selectedDay = { date: moment(new Date(this.preselectedDate.from)) };
          this.currentDate = moment(new Date(this.preselectedDate.from));
        } else {
          this.currentDate = moment();
        }
        if (this.preselectedDate.to) {
          this.selectedSecondDay = { date: moment(new Date(this.preselectedDate.to)) };
        }
      }
    } else {
      this.currentDate = moment();
    }
    if (this.maxDate && this.currentDate.isAfter(moment(this.maxDate), 'day')) {
      this.currentDate = moment(this.maxDate);
    }
    if (this.minDate && this.currentDate.isBefore(moment(this.minDate), 'day')) {
      this.currentDate = moment(this.minDate);
    }
    if (this.showTwoMonth) {
      this.nextDate = moment(this.currentDate).add(1, 'months');
    }
    this.buildCalendar();
  },
  methods: {
    prepareLocale() {
      const locale = this.$i18n.locale.replace('_', '-');
      moment.locale(locale);
      moment.updateLocale(locale, {
        week: {
          dow: 0,
          doy: 6,
        },
      });
      this.weekdays = (Vue as any).material.locale.shortDays;
      this.months = (Vue as any).material.locale.months;
    },
    prevMonth() {
      this.currentDate = moment(this.currentDate).subtract(1, 'months');
      if (this.showTwoMonth) {
        this.nextDate = moment(this.nextDate).subtract(1, 'months');
      }
      this.buildCalendar();
    },
    nextMonth() {
      this.currentDate = moment(this.currentDate).add(1, 'months');
      if (this.showTwoMonth) {
        this.nextDate = moment(this.nextDate).add(1, 'months');
      }
      this.buildCalendar();
    },
    buildCalendar(): void {
      const currentDates = this.buildWeek(this.currentDate);
      const currentWeeks: DatePickerDayModel[][] = [];
      while (currentDates.length > 0) {
        currentWeeks.push(currentDates.splice(0, 7));
      }
      this.currentWeeks = currentWeeks;
      if (this.showTwoMonth) {
        const nextDates = this.buildWeek(this.nextDate);
        const nextWeeks: DatePickerDayModel[][] = [];
        while (nextDates.length > 0) {
          nextWeeks.push(nextDates.splice(0, 7));
        }
        this.nextWeeks = nextWeeks;
      }
    },
    buildWeek(currentMoment: moment.Moment): DatePickerDayModel[] {
      let firstOfMonth = moment(currentMoment)
        .startOf('month')
        .day();
      const firstDayOfGrid = moment(currentMoment)
        .startOf('month')
        .subtract(firstOfMonth, 'days');
      const start = moment(firstDayOfGrid).startOf('week');
      const startDate = start.date();
      const month = currentMoment.month();
      let count = 35;
      const end = moment(start).add(count, 'days');
      let lastOfMonth = moment(currentMoment)
        .endOf('month')
        .date();
      if (month == end.month() && lastOfMonth != end.date() - 1) {
        count = 42;
      }
      const days = [];
      for (let i = startDate; i < startDate + count; i++) {
        const day = moment(firstDayOfGrid).date(i);
        days.push({
          date: day,
          selected: this.isSelected(day),
          today: this.isToday(day),
          currentM: day.month() === month,
          disabled: this.isDisabled(day),
        });
      }
      return days;
    },
    isToday(date: moment.Moment): boolean {
      return moment().isSame(moment(date), 'day');
    },
    isSelected(date: moment.Moment): boolean {
      return this.selectedDay && this.selectedDay.date
        ? moment(this.selectedDay.date).isSame(moment(date), 'day') ||
            (this.selectedSecondDay && this.selectedSecondDay.date
              ? moment(this.selectedSecondDay.date).isSame(moment(date), 'day')
              : false)
        : false;
    },
    isDisabled(date: moment.Moment): boolean {
      if (this.minDate && date.isBefore(moment(this.minDate), 'day')) {
        return true;
      } else if (this.maxRangeDate && date.isAfter(moment(this.maxRangeDate), 'day')) {
        return true;
      }
      return false;
    },
    selectDay(day: DatePickerDayModel): void {
      if (day.disabled) {
        return;
      }
      if (this.combinedSelector && this.targetScope) {
        if (this.checkinActive) {
          this.selectedDay = day;
        } else {
          this.selectedSecondDay = day;
        }
      } else {
        if (!this.rangePicker) {
          this.selectedDay = day;
        } else {
          if (!this.selectedDay.date || (this.selectedDay.date && this.selectedSecondDay.date)) {
            this.selectedDay = {};
            this.selectedSecondDay = {};
            this.selectedDay = day;
            if (day.date) {
              this.startDate = new Date(day.date.format(DateFormat.VueDate));
            }
          } else {
            this.hoveredDay = {};
            if (day.date && this.selectedDay.date && day.date.isBefore(this.selectedDay.date, 'day')) {
              this.selectedSecondDay = this.selectedDay;
              this.selectedDay = day;
            } else {
              this.selectedSecondDay = day;
            }
          }
        }
      }
      this.buildCalendar();
      this.saveResult();
    },
    hoverDay(day: DatePickerDayModel) {
      if (!day.disabled) {
        if (this.rangePicker && this.selectedDay && !this.selectedSecondDay) {
          this.hoveredDay = day;
        }
      } else {
        delete this.hoveredDay;
      }
    },
    saveResult() {
      if (this.selectedDay && this.selectedDay.date) {
        if (!this.rangePicker) {
          this.$emit('onSelectDate', this.selectedDay.date.format('YYYY-MM-DD'));
        } else if (this.selectedSecondDay && this.selectedSecondDay.date) {
          this.$emit('onSelectDate', {
            from: this.selectedDay.date.format(DateFormat.VueDate),
            to: this.selectedSecondDay.date.format(DateFormat.VueDate),
          });
        }
      }
    },
  },
});
